if (!customElements.get('slideshow-component')) {
  customElements.define(
    'slideshow-component',
    class SlideshowComponent extends HTMLElement {
      constructor() {
        super();
        this.sliderControls = this.querySelector('.swiper-controls');
        this.sliderInstance = false;
        this.layout = this.dataset.layout;
        this.items = Number(this.dataset.items);
        this.selectedIndex = this.selectedIndex;
        this._reiniting = false;
        this._onMatchMobile = () => this.reinitForViewport();
        this._onUnmatchMobile = () => this.reinitForViewport();
      }

      connectedCallback() {
        this.initSlider();
        if (this.classList.contains('slideshow-height--adapt')) {
          this.calcSlideHeight();
        }
        document.addEventListener('matchMobile', this._onMatchMobile);
        document.addEventListener('unmatchMobile', this._onUnmatchMobile);
      }

      disconnectedCallback() {
        document.removeEventListener('matchMobile', this._onMatchMobile);
        document.removeEventListener('unmatchMobile', this._onUnmatchMobile);
        if (this.sliderInstance && this.sliderInstance.slider) {
          try {
            this.sliderInstance.slider.destroy(true, true);
          } catch (e) {}
        }
      }

      static get observedAttributes() {
        return ['selected-index'];
      }

      get selectedIndex() {
        return parseInt(this.getAttribute('selected-index')) || 0;
      }

      set selectedIndex(index) {
        this.setAttribute('selected-index', `${index}`);
      }

      buildSliderOptions() {
        const additionModules = [FoxTheme.Swiper.Autoplay, FoxTheme.Swiper.EffectFade];
        const isMobile = !!(FoxTheme.config && FoxTheme.config.mqlMobile);

        // Fade + touch often fails on phones; use slide on mobile for reliable swipe.
        const effect = isMobile || this.layout === 'centered' ? 'slide' : 'fade';

        const options = {
          slidesPerView: 1,
          spaceBetween: 10,
          loop: true,
          grabCursor: true,
          allowTouchMove: true,
          simulateTouch: true,
          touchStartPreventDefault: false,
          threshold: 5,
          effect,
          on: {
            init: this.handleAfterInit.bind(this),
          },
        };

        if (effect === 'fade') {
          options.fadeEffect = { crossFade: true };
        }

        if (this.sliderControls) {
          const nextEl = this.sliderControls.querySelector('.swiper-button-next');
          const prevEl = this.sliderControls.querySelector('.swiper-button-prev');
          const paginationEl = this.sliderControls.querySelector('.swiper-pagination');

          if (paginationEl) {
            options.pagination = {
              el: paginationEl,
              clickable: true,
            };
          }

          if (nextEl && prevEl) {
            options.navigation = {
              nextEl,
              prevEl,
            };
          }
        }

        if (this.layout === 'centered') {
          Object.assign(options, {
            centeredSlides: true,
            slidesPerView: 'auto',
            effect: 'slide',
          });
        }

        if (this.layout !== 'centered' && this.items === 1) {
          options.loop = false;
          this.classList.remove('slideshow-controls-visible--yes');
        }

        const autoplayDelay = parseInt(this.dataset.autoplay);
        if (autoplayDelay > 0) {
          options.autoplay = {
            delay: autoplayDelay,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          };
        }

        return { options, additionModules };
      }

      initSlider() {
        const { options, additionModules } = this.buildSliderOptions();
        this.sliderOptions = options;

        this.sliderInstance = new window.FoxTheme.Carousel(this, this.sliderOptions, additionModules);
        this.sliderInstance.init();
        this.sliderInstance.slider.on('realIndexChange', this.handleSlideChange.bind(this));

        if (this.sliderInstance) {
          if (this.layout === 'centered' && !(FoxTheme.config && FoxTheme.config.mqlMobile)) {
            const motionTextElements = Array.from(this.querySelectorAll('motion-element[data-text]'));
            motionTextElements &&
              motionTextElements.forEach((motionTextElement) => {
                typeof motionTextElement.resetAnimation == 'function' &&
                  !motionTextElement.closest('.swiper-slide-active') &&
                  motionTextElement.resetAnimation();
              });
          }

          this.selectedElement = this.sliderInstance.slider.slides[this.sliderInstance.slider.activeIndex];
          this.onReady(this.selectedElement, this.sliderInstance.slider.slides);

          const focusableElements = FoxTheme.a11y.getFocusableElements(this);

          if (this.layout === 'centered') {
            focusableElements.forEach((element) => {
              element.addEventListener('focusin', () => {
                const slide = element.closest('.swiper-slide');
                const slideToIndex = this.sliderInstance.slider.slides.indexOf(slide);
                this.sliderInstance.slider.slideToLoop(slideToIndex);
              });
            });
          } else {
            focusableElements.forEach((element) => {
              element.addEventListener('focusin', () => {
                const slide = element.closest('.swiper-slide');
                this.sliderInstance.slider.slideTo(this.sliderInstance.slider.slides.indexOf(slide));
              });
            });
          }
        }
      }

      reinitForViewport() {
        if (this._reiniting) return;
        this._reiniting = true;
        try {
          if (this.sliderInstance && this.sliderInstance.slider) {
            try {
              this.sliderInstance.slider.destroy(true, true);
            } catch (e) {}
          }
          this.sliderInstance = false;
          this.initSlider();
        } finally {
          this._reiniting = false;
        }
      }

      onReady(selectedElement) {
        selectedElement.dataset.type === 'video' &&
          FoxTheme.utils.displayedMedia(selectedElement.querySelectorAll('video-element'))?.play();
        if (FoxTheme.config.motionReduced) return;
        selectedElement.querySelectorAll('motion-element').forEach((motionEl) => {
          setTimeout(() => {
            motionEl && motionEl.removeAttribute('hold');
            motionEl && motionEl.preInitialize();
            this.removeAttribute('data-media-loading');
            motionEl.initialize();
          });
        });
      }

      handleAfterInit() {
        if (this.sliderControls) {
          const activeBullet = this.sliderControls.querySelector('.swiper-pagination-bullet-active');
          if (activeBullet) {
            activeBullet.classList.remove('swiper-pagination-bullet-active');
            activeBullet.offsetHeight;
            activeBullet.classList.add('swiper-pagination-bullet-active');
          }
        }
      }

      handleSlideChange(swiper) {
        const { slides, realIndex, activeIndex } = swiper;
        this.selectedIndex = realIndex;
        this.updateControlsScheme(slides[activeIndex]);
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
          const fromElements = this.querySelectorAll(`[data-swiper-slide-index="${oldValue}"]`);
          const toElements = this.querySelectorAll(`[data-swiper-slide-index="${newValue}"]`);

          fromElements.forEach((fromElement) => {
            if (fromElement.dataset.type === 'video') {
              const videoElement = FoxTheme.utils.displayedMedia([fromElement.querySelector('video-element')]);
              videoElement && videoElement.pause();
            }
            const motionEls = fromElement.querySelectorAll('motion-element');
            motionEls &&
              motionEls.forEach((motionEl) => {
                motionEl.hasAttribute('data-text') && motionEl.resetAnimation();
              });
          });

          toElements.forEach((toElement) => {
            setTimeout(() => {
              if (toElement.classList.contains('swiper-slide-active')) {
                if (toElement.dataset.type === 'video') {
                  const videoElement = FoxTheme.utils.displayedMedia([toElement.querySelector('video-element')]);
                  videoElement && videoElement.play();
                }
                toElement.querySelectorAll('motion-element').forEach((motionEl) => {
                  motionEl && motionEl.refreshAnimation();
                });
              }
            });
          });
        }
      }

      updateControlsScheme(activeSlide) {
        if (this.sliderControls) {
          Array.from(this.sliderControls.classList)
            .filter((className) => className.startsWith('color-'))
            .forEach((className) => this.sliderControls.classList.remove(className));
          const colorScheme = activeSlide.dataset.colorScheme;
          this.sliderControls.classList.add(colorScheme);
        }
      }

      calcSlideHeight() {
        let maxHeight = 0;
        const slides = this.querySelectorAll('.swiper-slide');
        slides &&
          slides.forEach((slide) => {
            const slideText = slide.querySelector('.slideshow__text');
            if (slideText) {
              const slideTextHeight = slideText.offsetHeight;
              if (slideTextHeight > maxHeight) maxHeight = slideTextHeight;
            }
          });
        if (this.classList.contains('slideshow-controls-position--bottom')) {
          maxHeight += 100;
        }
        this.style.setProperty('--slide-height', maxHeight + 'px');
      }
    }
  );
}
